/**
 * AiAutosendCard - system_admin control for the requester domains (or
 * exact addresses, for clients on AOL / Gmail) on which an AI key may
 * send its draft reply without a human.
 *
 * Backs ``ai.autosend_domains`` in system_settings (see
 * backend/app/services/ai_autosend_service.py). The gate is the ticket
 * REQUESTER's domain; who else is on To / Cc does not matter. The key
 * must also hold ``replies: write``. Empty list = feature off.
 *
 * One domain or address per line. The server normalizes (lowercase,
 * drops a leading "@" or "*.") and rejects anything that is neither a
 * hostname nor an address,
 * so this card does no validation of its own beyond trimming.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert, Button, Card, Textarea } from "@/components/ui";
import api from "@/lib/api";
import type { AiAutosendPayload } from "@/types/api";

const QUERY_KEY = ["admin", "ai-autosend"] as const;

function parseLines(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((l) => l.trim())
    .filter(Boolean);
}

export function AiAutosendCard(): JSX.Element {
  const queryClient = useQueryClient();

  const query = useQuery<AiAutosendPayload>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const r = await api.get<AiAutosendPayload>("/admin/ai-autosend");
      return r.data;
    },
  });

  const [text, setText] = useState<string>("");
  const [savedRecently, setSavedRecently] = useState<boolean>(false);

  // Seed once per distinct server value so a background refetch does
  // not clobber an edit in progress (same guard as SlackAlertCard).
  const lastSeededRef = useRef<string | null>(null);
  useEffect(() => {
    if (!query.data) return;
    const sig = (query.data.domains ?? []).join("\n");
    if (sig === lastSeededRef.current) return;
    lastSeededRef.current = sig;
    setText(sig);
  }, [query.data]);

  const saver = useMutation({
    mutationFn: async (body: AiAutosendPayload) => {
      const r = await api.put<AiAutosendPayload>("/admin/ai-autosend", body);
      return r.data;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(QUERY_KEY, saved);
      setSavedRecently(true);
      window.setTimeout(() => setSavedRecently(false), 1500);
    },
  });

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    saver.mutate({ domains: parseLines(text) });
  }

  const current = query.data?.domains ?? [];
  const dirty =
    !!query.data && parseLines(text).join("\n") !== current.join("\n");
  const saveError =
    (saver.error as { response?: { data?: { detail?: string } } } | null)
      ?.response?.data?.detail ?? (saver.error as Error | null)?.message;

  return (
    <Card padding="md" data-testid="ai-autosend-card">
      <h3 className="text-base font-semibold text-neutral-900">
        AI Autonomous Replies
      </h3>
      <p className="mt-1 text-sm text-neutral-600">
        When a ticket&apos;s requester email is on one of these domains, or
        is one of these exact addresses, an AI API key with replies set to Read + write may send its draft
        reply without a human, exactly as if you clicked Send. Each send
        posts a notice to the Slack alerts channel. Capped at 3 sends per
        ticket and 50 in total per 24 hours. Leave empty to turn off.
      </p>

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <Textarea
          id="ai-autosend-domains"
          label="Allowed requester domains or addresses (one per line)"
          placeholder={"brownbagdirect.com\nnatlexp@aol.com"}
          helperText={
            current.length > 0
              ? `Currently on for ${current.length} entr${current.length === 1 ? "y" : "ies"}. A domain covers its subdomains; an address covers only that sender.`
              : "Currently off."
          }
          rows={5}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={query.isLoading}
          data-testid="ai-autosend-input"
        />

        {saver.isError && (
          <Alert variant="error">Save failed: {saveError ?? "unknown error"}</Alert>
        )}

        <div className="flex items-center gap-3">
          <Button
            type="submit"
            variant="primary"
            disabled={!dirty || saver.isPending}
            isLoading={saver.isPending}
            data-testid="ai-autosend-save"
          >
            Save
          </Button>
          {savedRecently && (
            <span className="text-sm text-emerald-700">Saved.</span>
          )}
        </div>
      </form>
    </Card>
  );
}

export default AiAutosendCard;
